"use client"

import type React from "react"
import { useState } from "react"
import type { ConnectorConfig, SetupData } from "../SetupFlow"
import "./ConnectorSelection.css"

interface ConnectorSelectionProps {
  hasExistingConnectors: boolean
  onSubmit: (connector: ConnectorConfig) => void
  onAddAnother: () => void
  setupData: SetupData
}

const ConnectorSelection: React.FC<ConnectorSelectionProps> = ({ hasExistingConnectors, onSubmit, onAddAnother }) => {
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null)

  const availableConnectors: ConnectorConfig[] = [
    {
      id: "hubspot-salesforce",
      name: "HubSpot to Salesforce",
      from: "HubSpot",
      to: "Salesforce",
      fromLogo: "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg",
      toLogo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
    },
    {
      id: "salesforce-hubspot",
      name: "Salesforce to HubSpot",
      from: "Salesforce",
      to: "HubSpot",
      fromLogo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
      toLogo: "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg",
    },
  ]

  const handleConnectorSelect = (connectorId: string) => {
    setSelectedConnector(connectorId)
  }

  const handleSubmit = () => {
    if (selectedConnector) {
      const connector = availableConnectors.find((c) => c.id === selectedConnector)
      if (connector) {
        onSubmit(connector)
      }
    }
  }

  if (hasExistingConnectors) {
    return (
      <div className="connector-selection existing-connectors">
        <div className="connector-selection-header">
          <h1>Your Connectors</h1>
          <p>Manage your existing connectors or add a new one.</p>
        </div>

        <div className="existing-connectors-list">
          <div className="existing-connector-card">
            <div className="connector-info">
              <div className="connector-logos">
                <div className="connector-logo">
                  <img src="https://cdn.worldvectorlogo.com/logos/hubspot-1.svg" alt="HubSpot" />
                </div>
                <div className="connector-arrow">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14" />
                    <path d="M12 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="connector-logo">
                  <img src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" alt="Salesforce" />
                </div>
              </div>
              <div className="connector-details">
                <h3>HubSpot to Salesforce</h3>
                <p>Active sync • Last synced 2 hours ago</p>
              </div>
            </div>
            <div className="connector-actions">
              <button className="manage-btn">Manage</button>
              <button className="settings-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="add-connector-section">
          <button className="add-connector-btn" onClick={onAddAnother}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Another Connector
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="connector-selection new-setup">
      <div className="connector-selection-header">
        <h1>Welcome to NetzSync!</h1>
        <p>To start syncing your data, please connect to your platforms.</p>
      </div>

      <div className="connector-options">
        {availableConnectors.map((connector) => (
          <div
            key={connector.id}
            className={`connector-card ${selectedConnector === connector.id ? "selected" : ""}`}
            onClick={() => handleConnectorSelect(connector.id)}
          >
            <div className="connector-checkbox">
              <input
                type="checkbox"
                checked={selectedConnector === connector.id}
                onChange={() => handleConnectorSelect(connector.id)}
              />
            </div>
            <div className="connector-content">
              <div className="connector-logos">
                <div className="connector-logo">
                  <img src={connector.fromLogo || "/placeholder.svg"} alt={connector.from} />
                </div>
                <div className="connector-arrow">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M19 12l-7 7-7-7" />
                  </svg>
                </div>
                <div className="connector-logo">
                  <img src={connector.toLogo || "/placeholder.svg"} alt={connector.to} />
                </div>
              </div>
              <div className="connector-details">
                <h3>{connector.name}</h3>
                <p>
                  Sync and manage your contacts, leads, and deals seamlessly between {connector.from} and {connector.to}
                  . Create, update, and edit records automatically to keep both platforms in sync.
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="connector-actions">
        <button className="next-button" onClick={handleSubmit} disabled={!selectedConnector}>
          Next
        </button>
      </div>
    </div>
  )
}

export default ConnectorSelection
